import React from 'react';
import { Chip, Paper } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { injectIntl } from 'react-intl';
import { IMatch, IMatchOdds } from '../typings';
import { getProject } from '../../../common/services/project';

interface IProps {
  match: IMatch;
  odds: IMatchOdds[];
  mainBookmakerCode: string | null;
  intl: any;
}

// Direction of the odds move since the previous import (a shorter odd = more
// likely outcome). Purely indicative for the admin.
const trend = (current?: number, previous?: number): string => {
  if (current == null || previous == null || current === previous) {
    return '=';
  }

  return current < previous ? '↓' : '↑';
};

// @ts-ignore
@injectIntl
export class MatchOddsView extends React.Component<IProps> {
  renderOddsRow(o: IMatchOdds) {
    const {
      intl: { messages: m },
    } = this.props;

    return (
      <div key={o.id} style={{ display: 'flex', gap: 24, padding: '4px 0' }}>
        <span style={{ minWidth: 80, fontWeight: 600 }}>
          {o.isLive ? m.bookmaker_odds_live : m.bookmaker_odds_prematch}
        </span>
        <span>
          1&nbsp;: {o.homeTeamWin} {trend(o.homeTeamWin, o.previousHomeTeamWin)}
        </span>
        <span>
          {m.bookmaker_odds_draw}&nbsp;: {o.draw} {trend(o.draw, o.previousDraw)}
        </span>
        <span>
          2&nbsp;: {o.awayTeamWin} {trend(o.awayTeamWin, o.previousAwayTeamWin)}
        </span>
      </div>
    );
  }

  render() {
    const {
      odds,
      mainBookmakerCode,
      intl: { messages: m },
    } = this.props;

    const docLink = (
      <Alert severity="info">
        <a href={`#${getProject().url}/documentation/bookmakers`} target="_blank" rel="noopener noreferrer">
          {m.read_the_docs}
        </a>
      </Alert>
    );

    if (!odds || odds.length === 0) {
      return (
        <div style={{ padding: 16 }}>
          {docLink}
          <div style={{ padding: 16 }}>
            <span>{m.bookmaker_odds_notFound}</span>
          </div>
        </div>
      );
    }

    // Group the rows by bookmaker, keeping prematch before live.
    const byBookmaker: { [code: string]: { bookmaker: IMatchOdds['bookmaker']; rows: IMatchOdds[] } } = {};
    odds.forEach(o => {
      const code = o.bookmaker.code;
      if (!byBookmaker[code]) {
        byBookmaker[code] = { bookmaker: o.bookmaker, rows: [] };
      }
      byBookmaker[code].rows.push(o);
    });

    return (
      <div style={{ padding: 16 }}>
        {docLink}
        {!mainBookmakerCode && (
          <Alert severity="warning" style={{ marginTop: 8 }}>
            {m.bookmaker_odds_noMainBookmaker}
          </Alert>
        )}
        {Object.keys(byBookmaker).map(code => {
          const { bookmaker, rows } = byBookmaker[code];
          const isMain = code === mainBookmakerCode;
          rows.sort((a, b) => Number(a.isLive) - Number(b.isLive));

          return (
            <Paper key={code} elevation={1} style={{ margin: '12px 0', padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Chip
                  label={bookmaker.name}
                  size="small"
                  style={{ backgroundColor: bookmaker.cssColor || undefined, color: '#fff' }}
                />
                {isMain && <Chip label={m.bookmaker_odds_main} size="small" color="primary" />}
                {bookmaker.affiliationLink && (
                  <a href={bookmaker.affiliationLink} target="_blank" rel="noopener noreferrer">
                    {bookmaker.affiliationText || m.bookmaker_odds_affiliation}
                  </a>
                )}
              </div>
              {rows.map(o => this.renderOddsRow(o))}
            </Paper>
          );
        })}
      </div>
    );
  }
}
